import { useEffect, useState } from "react";

import { EnhancedCustomerNote } from "../../../Models/CustomerNote";
import { Error } from "../../../Utils/Errors";
import { useApi } from "../../../Utils/useApi";
import PreviousCustomerNote from "../Types/PreviousCustomerNote";
import CustomerNotePanel, { ICustomerNotePanelConfig, ICustomerNotePanelProps } from "./CustomerNotePanel";

export interface ICustomerNotePanelWithPreviousNotesProps<TNoteTagProps>
  extends Omit<ICustomerNotePanelProps<TNoteTagProps>, "previousNotes" | "isLoading">,
    ICustomerNotePanelConfig<TNoteTagProps> {
  /** The id of the current session's note if one has been persisted. */
  currentNoteId: string | null;
  /** The id of the current session's customer if one exists. */
  customerId: string | null;
  /** The id of the current session's lead. */
  leadId: string;
}

/** An extension of {@link ICustomerNotePanelProps} provisions the ILoader and previousNotes props. */
const CustomerNotePanelWithPreviousNotes = <TNoteTagProps>({
  currentNoteId,
  customerId,
  leadId,
  ...rest
}: ICustomerNotePanelWithPreviousNotesProps<TNoteTagProps>) => {
  const [previousNotes, setPreviousNotes] = useState<PreviousCustomerNote[]>([]);

  const { response, isLoading } = useApi<EnhancedCustomerNote[]>({
    url: `Legacy/V0/contacts/${leadId}/customers/${customerId}/notes`,
    valid: Boolean(customerId),
    error: Error.GetCustomerNotes
  });

  useEffect(() => {
    if (!response) return;
    setPreviousNotes(
      response
        .filter(n => n.id !== currentNoteId)
        .map(n => new EnhancedCustomerNote(n))
        .sort(EnhancedCustomerNote.Comparator)
        .map(n => new PreviousCustomerNote(n))
    );
  }, [response]);

  useEffect(() => {
    if (!customerId) setPreviousNotes([]);
  }, [customerId]);

  return CustomerNotePanel({ ...rest, previousNotes, isLoading });
};

export default CustomerNotePanelWithPreviousNotes;
